import { Component, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { SalesFunnelDataService } from '../shared/sales-funnel-data.service';
import { ISalesFunnelLayoutChild } from '../shared/sales-funnel-child.module';
import { CreditCheckService } from '../services/credit-check-service';
import { SpeedTestService } from '../services/speed-test-service';

@Component({
  selector: 'app-customer-details-component',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './customer-details-component.html',
  styleUrls: ['./customer-details-component.scss'],
})
export class CustomerDetailsComponent implements OnInit, ISalesFunnelLayoutChild {
  private router = inject(Router);
  private dataService = inject(SalesFunnelDataService);

  private creditCheckService = inject(CreditCheckService);
  private speedTestService = inject(SpeedTestService);
  private static speedTestOk = false;

  customerForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    address: new FormControl('', [Validators.required]),
    addressLine1: new FormControl(''),
    addressLine2: new FormControl(''),
    postcode: new FormControl('', [Validators.required, Validators.pattern('[0-9]*')]),
    country: new FormControl('', Validators.required),

    billingName: new FormControl('', Validators.required),
    billingAddress: new FormControl('', Validators.required),
    billingSortCode: new FormControl('', Validators.required),
    billingIBAN: new FormControl('', Validators.required),
    billingDOB: new FormControl(new Date().toDateString(), Validators.required),
  });

  salesFunnelIndex = 2;

  ngOnInit(): void {
    const data = this.dataService.getData();
    if (!data.customer) {
      this.dataService.fetchCustomerData().subscribe((customer) => {
        if (customer) {
          this.customerForm.setValue({
            ...this.customerForm.getRawValue(),
            firstName: customer.firstName,
            lastName: customer.lastName,
            address: customer.address.address,
            addressLine1: customer.address.line1 ?? '',
            addressLine2: customer.address.line2 ?? '',
            postcode: customer.postcode,
            country: customer.country,
          });
        }
      });
    } else {
      this.customerForm.setValue({
        ...this.customerForm.getRawValue(),
        firstName: data.customer.firstName,
        lastName: data.customer.lastName,
        address: data.customer.address.address,
        addressLine1: data.customer.address.line1 ?? '',
        addressLine2: data.customer.address.line2 ?? '',
        postcode: data.customer.postcode,
        country: data.customer.country,
      });
    }
    if (data.billing) {
      const year = data.billing.DOB.getFullYear();
      const month = (data.billing.DOB.getMonth() + 1).toString().padStart(2, '0');
      const day = data.billing.DOB.getDate().toString().padStart(2, '0');
      this.customerForm.setValue({
        ...this.customerForm.getRawValue(),
        billingName: data.billing.name,
        billingAddress: data.billing.address,
        billingSortCode: data.billing.sortCode,
        billingIBAN: data.billing.iban,
        billingDOB: `${year}-${month}-${day}`,
      });
    }
    if (!CustomerDetailsComponent.speedTestOk) {
      this.customerForm.disable();
    }
  }

  saveDetails(): void {
    const formValues = this.customerForm.value;
    this.dataService.updateCustomer({
      firstName: formValues.firstName ?? '',
      lastName: formValues.lastName ?? '',
      address: {
        address: formValues.address ?? '',
        line1: formValues.addressLine1 ?? '',
        line2: formValues.addressLine2 ?? '',
      },
      postcode: formValues.postcode ?? '',
      country: formValues.country ?? '',
    });
    this.dataService.updateBilling({
      name: formValues.billingName ?? '',
      address: formValues.billingAddress ?? '',
      sortCode: formValues.billingSortCode ?? '',
      iban: formValues.billingIBAN ?? '',
      DOB: new Date(formValues.billingDOB ?? ''),
    });
  }

  sendOffer(): void {
    this.saveDetails();
    const billing = this.dataService.getData().billing;
    if (billing) {
      this.creditCheckService.runCreditCheck(billing).subscribe((_) => {
        // TODO: What happens here?
      });
    }
    this.router.navigate(['/customer-offer']);
  }

  goBack(): void {
    this.saveDetails();
    this.router.navigate(['/package-offer']);
  }

  runSpeedTest(): void {
    this.speedTestService.runSpeedTest().subscribe((success) => {
      if (success) {
        CustomerDetailsComponent.speedTestOk = true;
        this.customerForm.enable();
      }
    });
  }
}
